import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { subscribeToMessageInserts } from "@/lib/message-inserts-feed";
import { useAuth } from "@/contexts/AuthContext";

let audioContext: AudioContext | null = null;
let userGestureBound = false;

function getAudioContext(): AudioContext {
  if (!audioContext) audioContext = new AudioContext();
  // Browsers suspend AudioContext until a user gesture. Try to resume on every play.
  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {});
  }
  return audioContext;
}

/** Bind one-time listeners that resume audio on the first user interaction. */
function ensureUserGestureUnlock() {
  if (userGestureBound || typeof window === "undefined") return;
  userGestureBound = true;
  const unlock = () => {
    try {
      const ctx = getAudioContext();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
    } catch {}
  };
  window.addEventListener("click", unlock, { once: false, passive: true });
  window.addEventListener("keydown", unlock, { once: false, passive: true });
  window.addEventListener("touchstart", unlock, { once: false, passive: true });
}

function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Master gain - LOUD
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.85, now);
    masterGain.connect(ctx.destination);

    // WhatsApp-style two-tone "pop-pop" notification
    const osc1 = ctx.createOscillator();
    const g1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(600, now);
    osc1.frequency.exponentialRampToValueAtTime(1200, now + 0.08);
    g1.gain.setValueAtTime(0.9, now);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc1.connect(g1);
    g1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.15);

    // Tone 2: higher pop (delayed)
    const delay = 0.12;
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(800, now + delay);
    osc2.frequency.exponentialRampToValueAtTime(1400, now + delay + 0.08);
    g2.gain.setValueAtTime(0, now);
    g2.gain.setValueAtTime(0.9, now + delay);
    g2.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.18);
    osc2.connect(g2);
    g2.connect(masterGain);
    osc2.start(now + delay);
    osc2.stop(now + delay + 0.18);

    // Subtle click layer for punch
    const click = ctx.createOscillator();
    const cg = ctx.createGain();
    click.type = "square";
    click.frequency.setValueAtTime(1800, now);
    cg.gain.setValueAtTime(0.15, now);
    cg.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    click.connect(cg);
    cg.connect(masterGain);
    click.start(now);
    click.stop(now + 0.03);
  } catch {
    // Silent fail
  }
}

/**
 * Plays a distinct "ding-dong" alert when a conversation is assigned to you.
 * More urgent than the regular notification — two descending bell tones.
 */
function playAssignmentSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.9, now);
    masterGain.connect(ctx.destination);

    // Ding — high bell tone
    const ding = ctx.createOscillator();
    const dg = ctx.createGain();
    ding.type = "sine";
    ding.frequency.setValueAtTime(1046, now); // C6
    ding.frequency.exponentialRampToValueAtTime(1046, now + 0.15);
    dg.gain.setValueAtTime(0.8, now);
    dg.gain.exponentialRampToValueAtTime(0.3, now + 0.15);
    dg.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    ding.connect(dg);
    dg.connect(masterGain);
    ding.start(now);
    ding.stop(now + 0.4);

    // Harmonic overtone for bell quality
    const h1 = ctx.createOscillator();
    const hg1 = ctx.createGain();
    h1.type = "sine";
    h1.frequency.setValueAtTime(2092, now); // C7
    hg1.gain.setValueAtTime(0.25, now);
    hg1.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    h1.connect(hg1);
    hg1.connect(masterGain);
    h1.start(now);
    h1.stop(now + 0.2);

    // Dong — lower bell tone (delayed)
    const delay = 0.25;
    const dong = ctx.createOscillator();
    const dg2 = ctx.createGain();
    dong.type = "sine";
    dong.frequency.setValueAtTime(784, now + delay); // G5
    dg2.gain.setValueAtTime(0, now);
    dg2.gain.setValueAtTime(0.8, now + delay);
    dg2.gain.exponentialRampToValueAtTime(0.3, now + delay + 0.15);
    dg2.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.5);
    dong.connect(dg2);
    dg2.connect(masterGain);
    dong.start(now + delay);
    dong.stop(now + delay + 0.5);

    // Second harmonic for dong
    const h2 = ctx.createOscillator();
    const hg2 = ctx.createGain();
    h2.type = "sine";
    h2.frequency.setValueAtTime(1568, now + delay); // G6
    hg2.gain.setValueAtTime(0, now);
    hg2.gain.setValueAtTime(0.2, now + delay);
    hg2.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.25);
    h2.connect(hg2);
    hg2.connect(masterGain);
    h2.start(now + delay);
    h2.stop(now + delay + 0.25);
  } catch {
    // Silent fail
  }
}

/**
 * Listens globally for new incoming (contact) messages and plays a notification sound.
 * Also listens for conversation assignment changes and plays a distinct alert
 * when a conversation is assigned to the current human agent.
 */
export function useNotificationSound(selectedConversationId: string | null) {
  const { activeWorkspaceId, agent } = useAuth();
  const selectedRef = useRef(selectedConversationId);
  selectedRef.current = selectedConversationId;

  // Bind once: unlock AudioContext on first user gesture (browser autoplay policy)
  useEffect(() => {
    ensureUserGestureUnlock();
  }, []);

  // Keep a set of conversation IDs belonging to the current org
  const orgConvIdsRef = useRef<Set<string>>(new Set());
  // Track last seen message id for dedupe across realtime + polling fallback
  const lastSeenMsgIdRef = useRef<string | null>(null);

  // Fetch org conversation IDs
  useEffect(() => {
    if (!activeWorkspaceId) return;
    supabase
      .from('conversations')
      .select('id')
      .eq('organization_id', activeWorkspaceId)
      .is('deleted_at' as never, null)
      .then(({ data }) => {
        orgConvIdsRef.current = new Set((data || []).map(c => c.id));
      });
  }, [activeWorkspaceId]);

  // Listen for new incoming messages → pop-pop sound (Realtime)
  useEffect(() => {
    return subscribeToMessageInserts((msg) => {
      if (
        msg.sender_type === "contact" &&
        !msg.is_private_note &&
        orgConvIdsRef.current.has(msg.conversation_id)
      ) {
        if (lastSeenMsgIdRef.current && msg.id <= lastSeenMsgIdRef.current) return;
        lastSeenMsgIdRef.current = msg.id;
        playNotificationSound();
      }
    });
  }, [activeWorkspaceId]);

  // Polling fallback (5s): detects new contact messages even if Realtime drops
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;

    // Baseline so we don't fire on mount
    (async () => {
      const { data } = await supabase
        .from('messages')
        .select('id, created_at')
        .is('deleted_at' as never, null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (!cancelled && data && data[0]) lastSeenMsgIdRef.current = data[0].id;
    })();

    const interval = setInterval(async () => {
      if (orgConvIdsRef.current.size === 0) return;
      const ids = Array.from(orgConvIdsRef.current).slice(0, 500);
      const { data } = await supabase
        .from('messages')
        .select('id, sender_type, conversation_id, is_private_note, created_at')
        .in('conversation_id', ids)
        .eq('sender_type', 'contact')
        .is('deleted_at' as never, null)
        .order('created_at', { ascending: false })
        .limit(5);
      if (!data || data.length === 0) return;
      for (const m of [...data].reverse()) {
        if (lastSeenMsgIdRef.current && m.id <= lastSeenMsgIdRef.current) continue;
        lastSeenMsgIdRef.current = m.id;
        if (!m.is_private_note) playNotificationSound();
      }
    }, 30000);

    return () => { cancelled = true; clearInterval(interval); };
  }, [activeWorkspaceId]);

  // Listen for conversation assignment changes → ding-dong when assigned to me
  useEffect(() => {
    if (!activeWorkspaceId || !agent?.id) return;

    const agentId = agent.id;
    const assignChannel = supabase
      .channel(`conv-assign-sound-${activeWorkspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversations",
          filter: `organization_id=eq.${activeWorkspaceId}`,
        },
        (payload) => {
          const oldRow = payload.old as { assignee_id?: string | null };
          const newRow = payload.new as { assignee_id?: string | null; id: string };

          // Only trigger when assignee changes TO me (not already assigned to me)
          if (
            newRow.assignee_id === agentId &&
            oldRow.assignee_id !== agentId
          ) {
            playAssignmentSound();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(assignChannel);
    };
  }, [activeWorkspaceId, agent?.id]);

  // When new conversations appear, add them to the known set
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const convChannel = supabase
      .channel(`conv-ids-track-${activeWorkspaceId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "conversations",
          filter: `organization_id=eq.${activeWorkspaceId}`,
        },
        (payload) => {
          orgConvIdsRef.current.add((payload.new as any).id);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(convChannel); };
  }, [activeWorkspaceId]);
}
